import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import useIsRestricted from '@hooks/useIsRestricted';
import { css } from '@emotion/react';
import { toPairs } from 'lodash';
import useAuth from '@vl-core/useAuth';
import dynamic from 'next/dynamic';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useSessionOutcome } from '@hooks/useSessionOutcome';
import { RecommendationsFormItem } from '@components/Clinicians/Session/Components/RecommendationsFormItem';
import { AppointmentOutcomeFormItem } from '@components/Clinicians/Session/Components/AppointmentOutcomeFormItem';
import type { ApptActionCodes } from 'vl-common/src/schemas/getMetaAppointments';
import DischargeFields from '@components/Clinicians/Session/v2/Components/DischargeFields';
import type { SessionForm } from '@components/Clinicians/session-form';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import useApptReferral from '@components/Clinicians/Session/hooks/useApptReferral';
import SessionLoader from '@components/Clinicians/SessionLoader';
import { ActionAccordion } from './Components/ActionAccordion';
import AppointmentActionAlert from './Components/AppointmentActionAlert';
import { FaceToFaceFieldset } from './Components/FaceToFaceFieldset';
import { BookVirtualConsultationFields } from './Components/BookVirtualConsultationFields';

const InvestigationRequest = dynamic(() => import('@components/Clinicians/InvestigationRequest'), { ssr: false });

export const ACTION_KEYS = {
  DISCHG: 'appt_action_DISCHG',
  REFF2F: undefined,
  BKVCON: 'appt_action_BKVCON',
  INVREQ: undefined,
  REF2AE: undefined,
  TRCARE: undefined,
  PRESCR: undefined,
  PTFACT: undefined
} satisfies Record<ApptActionCodes, keyof SessionForm | undefined>;

export const Actions = () => {
  const { t } = useTranslation('common');
  const { user } = useAuth();

  const { appointment } = useSessionAppointment();
  const appointmentId = useSessionAppointmentId();

  const [warningMessage] = useState('');

  const [, setf2fComplete] = useSessionFormState('f2fComplete');

  const { appointmentVersion, availableActions } = useSessionOutcome();

  const isRestricted = useIsRestricted();

  const { referral } = useApptReferral();

  useEffect(() => {
    if (user?.restricted) return;

    const referralWithClinicianCreated = referral?.ref_clinician;
    const selfBookReferralCreated = appointment?.referral_created && appointment?.auth_granted;

    if (referralWithClinicianCreated || selfBookReferralCreated) {
      setf2fComplete(true);
    }
  }, [
    appointment?.referral_created,
    appointment.auth_granted,
    isRestricted,
    referral,
    user?.restricted,
    setf2fComplete,
    appointmentId
  ]);

  const appointmentActionComponents: Partial<Record<ApptActionCodes, React.ReactNode>> = {
    REFF2F: <FaceToFaceFieldset />,
    PRESCR: <RecommendationsFormItem />,
    DISCHG: <DischargeFields />,
    BKVCON: <BookVirtualConsultationFields />,
    INVREQ: <InvestigationRequest />,
    REF2AE: (
      <AppointmentActionAlert message={t('A task will be raised for the patient to be called within 72 hours.')} />
    ),
    TRCARE: (
      <AppointmentActionAlert
        message={t('Once the appointment is complete, the care of the patient will be transferred to.')}
      />
    ),
    PTFACT: (
      <AppointmentActionAlert
        message={t('Once the appointment is complete, the Physitrack access information will be sent to the patient.')}
      />
    )
  };

  if (appointmentVersion !== 2) return null;

  return (
    <div data-private="1" data-testid="actions-component" css={{ marginTop: 2, padding: '1rem 0' }}>
      <AppointmentOutcomeFormItem key={`appt-outcome-${appointmentId}`} />

      {warningMessage && <Alert message={warningMessage} type="info" css={css({ marginBottom: '1rem' })} showIcon />}

      <Suspense fallback={<SessionLoader message={t('Initialising appointment form...')} />}>
        {availableActions &&
          // toPairs here for better type inference only
          toPairs(availableActions).map(([, action]) => (
            <ActionAccordion
              key={`${action.appt_action_code}-${appointmentId}`}
              appointmentActionCode={action.appt_action_code}
              label={action.appt_action_code}
            >
              {action.appt_action_code in appointmentActionComponents &&
                appointmentActionComponents[action.appt_action_code]}
            </ActionAccordion>
          ))}
      </Suspense>
    </div>
  );
};
